import { useState, useEffect } from 'react';
import { formatDate } from '../../common/SharedFunctions';
import { LoanType } from '../common/LoanConstants';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import { GridSettingType, LoanStatusType } from '@apply/constants';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { getLoanTypeShortName, getStatusTypeShortName } from '../common/LoanFunctions';
import { borrowerTooltipText, addressTooltipText } from '../../common/SharedFunctions';
import { Tooltip } from 'devextreme-react/tooltip';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { formatLoanTermShort } from '../common/functions/getLoanTermOptions';

const onTermCellRender = (e) =>
    formatLoanTermShort(
        e.data.loanTypeId,
        e.data.initialTermMonths,
        e.data.contractualExtension1Months,
        e.data.contractualExtension2Months
    );

const LinkLoanTable = ({ gridStore, searchText, onSelect }) => {
    const loanApi = new LoanApi.LoanApi(applicationInsights);

    const [dataStore, set_dataStore] = useState(gridStore?.store);
    const [tooltipId, set_tooltipId] = useState(null);

    const styles = {
        red: { color: 'red' },
    };

    const dataFunction = (criteria) => {
        return loanApi.getGrid_linkLoanToBClient(criteria);
    };

    useEffect(() => {
        if (gridStore && searchText) {
            gridStore.setOnLoad('id', dataFunction, searchText);
            set_dataStore(gridStore.store);
        }
    }, [gridStore, searchText]);

    const approvalDataCellRender = (e) => {
        return (
            <>
                {!e.data.approvalDate && 'Not Set'}
                {e.data.approvalDate && formatDate(e.data.approvalDate)}{' '}
                {e.data.daysSinceApproval > 0 && (
                    <span style={e.data.daysSinceApproval > 44 ? styles.red : null}> +{e.data.daysSinceApproval}</span>
                )}
            </>
        );
    };

    const actionCellRender = (e) => {
        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='Link Loan'
                onClick={(event) => onActionClicked(event, e.data)}
            >
                Select
            </button>
        );
    };

    const onActionClicked = (event, loan) => {
        event.preventDefault();
        event.stopPropagation();

        onSelect && onSelect(loan);
    };

    const onBorrowerCellRender = (e) => {
        const targetId = `borrower_${e.data.id}`;
        const text = borrowerTooltipText(e.data.borrower);
        return (
            <div
                id={targetId}
                onMouseEnter={(e) => set_tooltipId(targetId)}
                onMouseLeave={(e) => set_tooltipId(null)}
            >
                {e.data.borrowerEntity}
                <Tooltip
                    target={'#' + targetId}
                    visible={tooltipId === targetId}
                    hideOnOutsideClick={false}
                >
                    {text && <span>{text}</span>}
                </Tooltip>
            </div>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `state_${e.data.id}`;
        if (e.data.loanTypeId === LoanType.MasterNote) {
            const text = addressTooltipText(e.data.borrower.address, 'Borrower Address');
            return (
                <div
                    id={targetId}
                    onMouseEnter={(e) => set_tooltipId(targetId)}
                    onMouseLeave={(e) => set_tooltipId(null)}
                >
                    {e.data.borrowerState}
                    <Tooltip
                        target={'#' + targetId}
                        visible={tooltipId === targetId}
                        hideOnOutsideClick={false}
                    >
                        {text && <span>{text}</span>}
                    </Tooltip>
                </div>
            );
        } else {
            const text = addressTooltipText(e.data.address, 'Property Address');
            return (
                <div
                    id={targetId}
                    onMouseEnter={(e) => set_tooltipId(targetId)}
                    onMouseLeave={(e) => set_tooltipId(null)}
                >
                    {e.data.propertyState}
                    <Tooltip
                        target={'#' + targetId}
                        visible={tooltipId === targetId}
                        hideOnOutsideClick={false}
                    >
                        {text && <span>{text}</span>}
                    </Tooltip>
                </div>
            );
        }
    };

    const onLoanTypeCellRender = (e) => {
        if (e.data.isRental) {
            return getLoanTypeShortName(LoanType.Rental);
        }
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onStatusTypeCellRender = (e) => {
        return getStatusTypeShortName(e.data.statusId);
    };

    return (
        <BfiDataGrid
            gridType={GridSettingType.LinkLoanTable}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            allowedPageSizes={[10]}
            defaultPageSize={10}
        >
            <Column
                dataField='loanTypeId'
                caption='Type'
                cellRender={onLoanTypeCellRender}
                alignment='left'
                width={80}
            />
            <Column
                dataField='statusId'
                caption='Status'
                cellRender={onStatusTypeCellRender}
                alignment='left'
                width={70}
            />
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                minWidth={120}
            />
            <Column
                dataField='propertyState'
                caption='State'
                cellRender={onStateCellRender}
                width={50}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            <Column
                dataField='approvalDate'
                caption='Approved'
                cellRender={approvalDataCellRender}
                width={120}
            />
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onTermCellRender}
            />
            {LoanCommitOrApprovalColumn({ statusId: LoanStatusType.Outstanding })}
            <Column
                dataField='broker'
                caption='Broker'
                dataType='string'
                width={100}
            />
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={100}
            />
            <Column
                alignment='center'
                cellRender={actionCellRender}
                width={70}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
            />
        </BfiDataGrid>
    );
};

export default LinkLoanTable;
